import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import TenantUsers from '../components/TenantUsers';
import TenantFeaturesTab from '../components/TenantFeaturesTab';
import { apiService } from '../services/api';

function TenantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = React.useState('1');
  const [tenant, setTenant] = useState<any>(null);
  const [userCount, setUserCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);

  useEffect(() => {
    loadTenantData();
  }, [id]);

  const loadTenantData = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      
      // テナント詳細を直接取得するAPIを呼び出す
      const response = await apiService.getTenantDetail(id) as any;
      
      if (response?.success && response?.data) {
        setTenant(response.data);
        
        // stats内のユーザー数を使用
        if (response.data?.stats) {
          const userCount = response.data.stats.current_users || 0;
          setUserCount(userCount);
        } else {
          setUserCount(0);
        }
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to load tenant data:', error);
      setLoading(false);
    }
  };

  const handleSuspend = async () => {
    // TODO: 実際の一時停止処理を実装
    console.log('Suspending tenant:', id);
    setSuspendDialogOpen(false);
    // ここでAPIを呼び出してテナントを一時停止
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>読み込み中...</Typography>
      </Box>
    );
  }

  if (!tenant) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/tenants')}
            sx={{ mr: 2 }}
          >
            戻る
          </Button>
          <Typography variant="h4">テナントが見つかりません</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/tenants')}
          sx={{ mr: 2 }}
        >
          戻る
        </Button>
        <Typography variant="h4">{tenant.name}</Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              基本情報
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary">
                サブドメイン
              </Typography>
              <Typography variant="body1" gutterBottom>
                {tenant.subdomain}
              </Typography>

              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                プラン
              </Typography>
              <Chip label={tenant.plan} color="primary" size="small" />

              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                ステータス
              </Typography>
              <Chip
                label={tenant.status === 'active' ? 'アクティブ' : '停止中'}
                color={tenant.status === 'active' ? 'success' : 'error'}
                size="small"
              />

              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                管理者メール
              </Typography>
              <Typography variant="body1" gutterBottom>
                {tenant.admin_email}
              </Typography>

              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                作成日
              </Typography>
              <Typography variant="body1">
                {new Date(tenant.created_at).toLocaleDateString('ja-JP')}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <TabContext value={tabValue}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <TabList onChange={(e, v) => setTabValue(v)}>
                  <Tab label="使用状況" value="1" />
                  <Tab label="ユーザー" value="2" />
                  <Tab label="機能管理" value="3" />
                  <Tab label="危険な操作" value="4" sx={{ color: 'error.main' }} />
                </TabList>
              </Box>
              <TabPanel value="1">
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      ユーザー数
                    </Typography>
                    <Typography variant="h5">
                      {userCount} / {tenant.max_users}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      ストレージ使用量
                    </Typography>
                    <Typography variant="h5">
                      {(tenant.storage_used_mb / 1024).toFixed(2)} /{' '}
                      {tenant.max_storage_gb} GB
                    </Typography>
                  </Grid>
                </Grid>
              </TabPanel>
              <TabPanel value="2">
                <TenantUsers tenantId={id} />
              </TabPanel>
              <TabPanel value="3">
                <TenantFeaturesTab tenantId={id} tenantName={tenant.name} />
              </TabPanel>
              <TabPanel value="4">
                <Box>
                  <Typography variant="h6" color="error" gutterBottom>
                    危険な操作
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                    以下の操作は元に戻すことができません。慎重に実行してください。
                  </Typography>
                  <Box sx={{ 
                    p: 3, 
                    border: 1, 
                    borderColor: 'error.main', 
                    borderRadius: 1,
                    bgcolor: 'error.lighter',
                    backgroundColor: 'rgba(211, 47, 47, 0.08)'
                  }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      テナントの一時停止
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      このテナントを一時停止すると、すべてのユーザーがアクセスできなくなります。
                      再度有効化するまで、データの閲覧や編集はできません。
                    </Typography>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => setSuspendDialogOpen(true)}
                    >
                      テナントを一時停止
                    </Button>
                  </Box>
                </Box>
              </TabPanel>
            </TabContext>
          </Paper>
        </Grid>
      </Grid>

      {/* 一時停止確認ダイアログ */}
      <Dialog
        open={suspendDialogOpen}
        onClose={() => setSuspendDialogOpen(false)}
        aria-labelledby="suspend-dialog-title"
      >
        <DialogTitle id="suspend-dialog-title" sx={{ color: 'error.main' }}>
          テナントの一時停止確認
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{tenant.name}</strong> を本当に一時停止しますか？
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            この操作により、以下の影響があります：
          </DialogContentText>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <li>すべてのユーザーがログインできなくなります</li>
            <li>APIアクセスがブロックされます</li>
            <li>データの閲覧・編集ができなくなります</li>
          </Box>
          <DialogContentText sx={{ mt: 2, fontWeight: 'bold' }}>
            この操作を続行しますか？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuspendDialogOpen(false)} autoFocus>
            キャンセル
          </Button>
          <Button onClick={handleSuspend} color="error" variant="contained">
            一時停止する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TenantDetail;